import {
  AbsoluteWrapper,
  Container,
  FlexAlignCenterSpaceBetween,
  Heading,
  InputWrapper,
  Row,
  SearchWrapper,
} from './SupplierOrdersPage.styled';
import { useNavigate } from 'react-router-dom';
import Input from 'components/Input/Input';
import { useTranslation } from 'react-i18next';

import Loader from 'components/Loader/Loader';
import Button from 'components/Button/Button';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { useRef, useState } from 'react';
import { useGetSupplierOrdersInfinite } from './hooks';
import { ORDERS_PER_PAGE, tableConfig } from './constants';
import { formatOrdersTableData } from 'components/Table/tableDataFormatter';
import { useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import { SortDirection } from 'components/Table/types';
import { IOrderDTO, IOrderResponseDTO } from 'types/Orders.types';
import Table from 'components/Table/Table';
import { RoutesConfig } from 'navigation/routes';
import { AppMode } from 'store/Common/reducers/common';
import { usePageTitle } from 'utils/hooks/usePageTitle';
import { useDebounce } from 'utils/hooks/useDebounce';
import { getDataFromPages } from 'utils/getDataFromPages';
import { useScrollRestoration } from 'utils/hooks/useScrollRestoration';
import { useElementHeight } from 'utils/hooks/useElementHeight';

const SupplierOrdersPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchBy, setSearchBy] = useState<string>('');
  const debouncedSearchBy = useDebounce(searchBy);
  const [sortBy, setSortBy] = useState<string | undefined>('');
  const [sortDirection, setSortDirection] = useState<SortDirection | undefined>(
    undefined
  );

  const companyId = useSelector(
    (state: IRootReducerState) => state.userInfo.company.id
  );

  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );

  usePageTitle(t('Orders'), true);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetSupplierOrdersInfinite(
      appMode,
      companyId,
      ORDERS_PER_PAGE,
      debouncedSearchBy,
      sortBy,
      sortDirection
    );

  // Opening an order and coming back should land on the same row, which only
  // works because the pages the user scrolled through are still cached here.
  const { visitedRowId, rememberVisitedRow } = useScrollRestoration(
    RoutesConfig.SupplierOrders.fullPath,
    !isLoading,
    [debouncedSearchBy, sortBy, sortDirection]
  );

  const orders: IOrderResponseDTO[] = getDataFromPages(data?.pages, 'orders');
  const tableData: IOrderDTO[] = formatOrdersTableData(orders, appMode);

  const stickyRowRef = useRef<HTMLDivElement>(null);
  const stickyRowHeight = useElementHeight(stickyRowRef);

  const handleViewButtonClick = (rowData: any) => {
    rememberVisitedRow(rowData.navigationID);
    navigate(
      RoutesConfig.SupplierSingleOrder.fullPath.replace(
        ':id',
        rowData.navigationID
      )
    );
  };

  return (
    <Container>
      <Row ref={stickyRowRef}>
        <FlexAlignCenterSpaceBetween>
          <SearchWrapper>
            <Heading>{t('Orders')}</Heading>
            <InputWrapper>
              <Input
                icon={MagnifyingGlass}
                placeholder={t('Search')}
                width={'100%'}
                height="41rem"
                value={searchBy}
                onChange={(e) => setSearchBy(e.target.value)}
                wrapperStyles={{ width: '100%' }}
              />
            </InputWrapper>
          </SearchWrapper>
          <Button
            width="140rem"
            primary
            onClick={() => navigate(RoutesConfig.SupplierNewOrder.fullPath)}
            label={t('New order')}
          />
        </FlexAlignCenterSpaceBetween>
      </Row>
      <Table
        perPage={ORDERS_PER_PAGE}
        isLoading={isLoading}
        tableData={tableData}
        tableConfig={tableConfig}
        hasExtraAction={true}
        onViewButtonClick={handleViewButtonClick}
        sortParams={{ sortBy, setSortBy, sortDirection, setSortDirection }}
        isInfiniteScroll
        isStickyHeader
        stickyHeaderOffset={stickyRowHeight}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        highlightedRowId={visitedRowId}
      />
      {isLoading ? (
        <AbsoluteWrapper>
          <Loader />
        </AbsoluteWrapper>
      ) : null}
    </Container>
  );
};

export default SupplierOrdersPage;
